/**
 * Dashboard Page
 * ==============
 * Live dashboard with aggregated platform metrics.
 * Fetches data from GET /api/v1/dashboard/stats.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions, useDashboard } from '../hooks';
import { Card, CardHeader, CardBody, Badge, Alert, Button } from '../components/ui';
import { DashboardLayout } from '../components/layout';

/**
 * Map lead status values to human-readable labels
 */
const STATUS_LABELS = {
  new: 'New',
  meeting_scheduled: 'Meeting Scheduled',
  contacted: 'Contacted',
  followup: 'Follow-up',
  questionnaire_completed: 'Questionnaire Completed',
  pre_proposal: 'Pre-Proposal',
  proposal_generation: 'Proposal Generation',
  qualified: 'Qualified',
  converted: 'Converted',
};

/**
 * Map lead status to badge variant
 */
const STATUS_BADGE_VARIANT = {
  new: 'info',
  meeting_scheduled: 'primary',
  contacted: 'accent',
  followup: 'warning',
  questionnaire_completed: 'success',
  pre_proposal: 'accent',
  proposal_generation: 'primary',
  qualified: 'success',
  converted: 'success',
};

/**
 * Map proposal status to badge variant
 */
const PROPOSAL_BADGE_VARIANT = {
  draft: 'default',
  pending_qa: 'warning',
  approved: 'success',
  sent_to_client: 'primary',
  accepted: 'success',
  rejected: 'error',
  expired: 'default',
};

const TIER_LABELS = {
  tier_1: 'Tier 1',
  tier_2: 'Tier 2',
  tier_3: 'Tier 3',
  tier_4: 'Tier 4',
  tier_5: 'Tier 5',
  unassigned: 'Unassigned',
};

const SOURCE_LABELS = {
  mock: 'Mock',
  crm: 'CRM',
  manual: 'Manual',
};

/**
 * Format a date string to a short display format
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const toYMD = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Loading skeleton for the dashboard
 */
const DashboardSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    {/* Stat cards skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                <div className="h-8 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
    {/* KPI cards skeleton */}
    <div>
      <div className="h-6 w-36 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i}>
            <CardBody>
              <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
    {/* Status breakdown skeleton */}
    <div>
      <div className="h-6 w-44 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <CardBody>
              <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-8 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
    {/* Tables skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {[1, 2].map((i) => (
        <Card key={i}>
          <CardBody>
            <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
            {[1, 2, 3, 4, 5].map((j) => (
              <div key={j} className="h-10 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            ))}
          </CardBody>
        </Card>
      ))}
    </div>
  </div>
);

/**
 * Progress bar breakdown component
 */
const BreakdownCard = ({ title, items, labelMap, total }) => (
  <Card>
    <CardBody>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      {items.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm">No data</p>
      ) : (
        <div className="space-y-3">
          {[...items]
            .sort((a, b) => b.count - a.count)
            .map((item) => {
              const key = item.source || item.tier;
              const label = labelMap[key] || key;
              const pct = total ? Math.round((item.count / total) * 100) : 0;
              return (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300">{label}</span>
                    <span className="text-gray-500 dark:text-gray-400">{item.count} ({pct}%)</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-300"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </CardBody>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, can } = usePermissions();
  const { stats, loading, error, refetch, startDate, endDate, setDateRange } = useDashboard();

  // Compute total leads for percentage calculation
  const totalLeadsCount = stats?.leads_by_status?.reduce((sum, item) => sum + item.count, 0) || 0;

  const handlePreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    setDateRange(toYMD(start), toYMD(end));
  };

  // Determine active preset for button highlighting
  const getActivePreset = () => {
    const today = toYMD(new Date());
    if (endDate !== today) return null;
    for (const days of [7, 30, 90]) {
      const s = new Date();
      s.setDate(s.getDate() - (days - 1));
      if (startDate === toYMD(s)) return days;
    }
    return null;
  };
  const activePreset = getActivePreset();

  const presetBtn = (days, label) => (
    <button
      onClick={() => handlePreset(days)}
      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
        activePreset === days
          ? 'bg-primary text-white border-primary'
          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
            Dashboard
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Overview of your activities and quick access to key features
          </p>
        </div>

        {/* Date Range Selection */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          {presetBtn(7, '7d')}
          {presetBtn(30, '30d')}
          {presetBtn(90, '90d')}
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setDateRange(e.target.value, endDate)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
            <span className="text-gray-500 dark:text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setDateRange(startDate, e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">

          {/* Loading State */}
          {loading && !stats && <DashboardSkeleton />}

          {/* Error State */}
          {error && !stats && (
            <Card>
              <CardBody>
                <div className="py-4">
                  <Alert
                    variant={error.code === 'NETWORK_ERROR' ? 'info' : 'error'}
                    title={error.code === 'NETWORK_ERROR' ? 'Connection Issue' : 'Failed to Load Dashboard'}
                  >
                    {error.message || 'An unexpected error occurred.'}
                  </Alert>
                  <div className="mt-4 text-center">
                    <Button onClick={refetch}>Retry</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Dashboard Data */}
          {stats && (
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card hover>
                  <CardBody>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Leads</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_leads}</p>
                      </div>
                      <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card hover>
                  <CardBody>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Proposals</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_proposals}</p>
                      </div>
                      <div className="w-12 h-12 bg-accent-50 dark:bg-accent-900/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card hover>
                  <CardBody>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Meetings</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_meetings}</p>
                      </div>
                      <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {isAdmin && stats.total_users != null && (
                  <Card hover>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Users</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_users}</p>
                        </div>
                        <div className="w-12 h-12 bg-accent-50 dark:bg-accent-900/20 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>

              {/* Business KPIs */}
              {stats.kpis && (
                <div>
                  <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white mb-4">Business KPIs</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                    <Card>
                      <CardBody>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Leads/Day</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.kpis.avg_leads_per_day}</p>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Meetings/Lead</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.kpis.avg_meetings_per_lead}</p>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Proposals/Lead</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.kpis.avg_proposals_per_lead}</p>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Conversion Rate</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.kpis.conversion_rate}%</p>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Time to Qualify</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {stats.kpis.avg_time_to_qualify_hours != null ? `${stats.kpis.avg_time_to_qualify_hours}h` : '-'}
                        </p>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg AI Spend/Lead</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">${stats.kpis.avg_ai_spend_per_lead_usd}</p>
                      </CardBody>
                    </Card>
                  </div>

                  {/* Breakdown cards */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <BreakdownCard
                      title="Leads by Source"
                      items={stats.kpis.leads_by_source}
                      labelMap={SOURCE_LABELS}
                      total={stats.total_leads}
                    />
                    <BreakdownCard
                      title="Leads by Tier"
                      items={stats.kpis.leads_by_tier}
                      labelMap={TIER_LABELS}
                      total={stats.total_leads}
                    />
                  </div>
                </div>
              )}

              {/* Pipeline Breakdown — matches stat card style */}
              {stats.leads_by_status && stats.leads_by_status.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Pipeline Breakdown</h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{totalLeadsCount} total</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
                    {stats.leads_by_status
                      .sort((a, b) => b.count - a.count)
                      .map((item) => (
                        <Card hover key={item.status}>
                          <CardBody>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                              {STATUS_LABELS[item.status] || item.status}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.count}</p>
                          </CardBody>
                        </Card>
                      ))}
                  </div>
                </div>
              )}

              {/* Recent Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Leads */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Recent Leads</h2>
                      {(can.createLead() || can.readLead()) && (
                        <button
                          onClick={() => navigate('/leads')}
                          className="text-sm text-primary hover:text-primary-600 transition-colors"
                        >
                          View all
                        </button>
                      )}
                    </div>
                  </CardHeader>
                  <CardBody>
                    {stats.recent_leads && stats.recent_leads.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Name</th>
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Company</th>
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.recent_leads.map((lead) => (
                              <tr
                                key={lead.id}
                                className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-[#2E3033] cursor-pointer transition-colors"
                                onClick={() => navigate(`/leads/${lead.id}`)}
                              >
                                <td className="py-2.5 px-2 text-gray-900 dark:text-white font-medium">{lead.name}</td>
                                <td className="py-2.5 px-2 text-gray-600 dark:text-gray-300">{lead.company}</td>
                                <td className="py-2.5 px-2">
                                  <Badge variant={STATUS_BADGE_VARIANT[lead.status] || 'default'}>
                                    {STATUS_LABELS[lead.status] || lead.status}
                                  </Badge>
                                </td>
                                <td className="py-2.5 px-2 text-gray-500 dark:text-gray-400">{formatDate(lead.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No leads yet</p>
                    )}
                  </CardBody>
                </Card>

                {/* Recent Proposals */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Recent Proposals</h2>
                      {(can.readProposal() || can.generateProposal()) && (
                        <button
                          onClick={() => navigate('/proposals')}
                          className="text-sm text-primary hover:text-primary-600 transition-colors"
                        >
                          View all
                        </button>
                      )}
                    </div>
                  </CardHeader>
                  <CardBody>
                    {stats.recent_proposals && stats.recent_proposals.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Title</th>
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Lead</th>
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                              <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.recent_proposals.map((proposal) => (
                              <tr
                                key={proposal.id}
                                className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-[#2E3033] cursor-pointer transition-colors"
                                onClick={() => navigate(`/proposals/${proposal.id}`)}
                              >
                                <td className="py-2.5 px-2 text-gray-900 dark:text-white font-medium truncate max-w-[150px]">
                                  {proposal.title || 'Untitled'}
                                </td>
                                <td className="py-2.5 px-2 text-gray-600 dark:text-gray-300">{proposal.lead_name}</td>
                                <td className="py-2.5 px-2">
                                  <Badge variant={PROPOSAL_BADGE_VARIANT[proposal.status] || 'default'}>
                                    {(proposal.status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                  </Badge>
                                </td>
                                <td className="py-2.5 px-2 text-gray-500 dark:text-gray-400">{formatDate(proposal.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No proposals yet</p>
                    )}
                  </CardBody>
                </Card>
              </div>
            </>
          )}

        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
